import React, { ReactElement, useEffect } from 'react';
import { Grid, Link, Typography } from '@mui/material';
import { AccountType } from 'constants/AccountType';
import { AuthTemplate } from 'components/templates/AuthTemplate';
import { StyledPageSubTitle, StyledPageTitle } from 'theme/styles';
import { useForm } from 'react-hook-form';
import {
  IAccountCreateFormValues,
  initialCommercialOrganisationCreateFormValues,
  initialIndividualCreateFormValues,
} from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormInput } from 'components/molecules/FormInput';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import { ERouteLinks } from 'models/route';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { add } from 'date-fns';
import { useAccount } from 'models/accountContext';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useTranslation } from 'react-i18next';
import useCreateAccountFormSchema from './schemaValidation';

interface ICreateAnAccount {
  type: AccountType | null;
  changePage: (p: number) => void;
  setData: (IData: any) => void;
}

const CreateAnAccount = ({ type, changePage, setData }: ICreateAnAccount): ReactElement => {
  const { t } = useTranslation();
  const { phoneCode, getPhoneCode } = useDictionary();
  const { schemaIndividual, schemaCommercialOrganisation } = useCreateAccountFormSchema();
  const defaultValues =
    type === AccountType.individual
      ? initialIndividualCreateFormValues
      : initialCommercialOrganisationCreateFormValues;
  const schema =
    type === AccountType.individual ? schemaIndividual : schemaCommercialOrganisation;
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues, resolver: yupResolver(schema) });
  const { createAccount, createMessage, isCreateLoading, clearCreateErrors } = useAccount();
  const { setAlert } = useAlert();

  useEffect(() => {
    if (!phoneCode) {
      getPhoneCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (setValue as any)('type', type);
  }, [type]);

  useEffect(
    () => () => {
      clearCreateErrors();
    },
    [],
  );

  const setAlertCrateSuccess = (text: string) => {
    setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
      text,
      variant: EAlertVariants.success,
    });
  };

  const onSubmit = (data: IAccountCreateFormValues) => {
    createAccount(data, (res: boolean) => {
      if (res) {
        setData({ email: data.email, type: data.type });
        setAlertCrateSuccess(t('page.createAccount.create.success'));
        changePage(2);
      }
    });
  };

  return (
    <AuthTemplate>
      <Grid item container xs={12} gap={7}>
        <Grid item container xs={12} gap={1}>
          <Grid item xs={12}>
            <StyledPageTitle variant="h4">{t(`page.createAccount.${type}`)}</StyledPageTitle>
          </Grid>
          <Grid item xs={12}>
            <StyledPageSubTitle variant="body2">
              {t(`page.createAccount.${type}.subtitle`)}
            </StyledPageSubTitle>
          </Grid>
        </Grid>

        <form onSubmit={handleSubmit(onSubmit as any)} style={{ width: '100%' }}>
          <Grid item container xs={12} gap={3}>
            <Grid item xs={12} gap={2}>
              <Grid item container xs={12}>
                <Grid item xs={12}>
                  <FormInput
                    name="email"
                    label={t('page.createAccount.input.email')}
                    placeholder={t('page.createAccount.input.email')}
                    control={control}
                    errors={errors}
                  />
                </Grid>
                <Grid item container xs={12} columnSpacing={3}>
                  <Grid item xs={4}>
                    <FormSelect
                      name="prefixPhoneNumberUniqueId"
                      options={mapDictionaryToOptionProp(phoneCode)}
                      label={t('page.createAccount.input.prefix')}
                      control={control}
                      setValue={setValue}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={8}>
                    <FormInput
                      name="phoneNumber"
                      label={t('page.createAccount.input.phone')}
                      placeholder={t('page.createAccount.input.phone')}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                </Grid>
                {type === AccountType.individual && (
                  <>
                    <Grid item xs={12}>
                      <FormDatePicker
                        name="dateOfBirth"
                        openTo="year"
                        label={t('page.createAccount.input.birth')}
                        placeholder={t('page.createAccount.input.birth')}
                        control={control}
                        errors={errors}
                        maxDate={add(new Date(), {
                          days: -1,
                        })}
                      />
                    </Grid>
                    <Grid item container alignItems="baseline" xs={12}>
                      <FormCheckbox
                        name="termsAndConditions"
                        labelElement={
                          <Typography variant="subtitle2">
                            {t('page.createAccount.check.termsAndConditions1')}{' '}
                            <Link
                              href={`${ERouteLinks.TermsAndConditions}`}
                              underline="none"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t('page.createAccount.check.termsAndConditions2')}
                            </Link>{' '}
                            {t('page.createAccount.check.termsAndConditions3')}
                          </Typography>
                        }
                        control={control}
                        errors={errors}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            {createMessage.text && (
              <Grid item xs={12} mb={2}>
                <Alert text={createMessage.text} variant={createMessage.variant} />
              </Grid>
            )}

            <Grid item container xs={12} gap={1.3} justifyItems="center">
              <Grid item xs={12}>
                <Button
                  id="login"
                  type="submit"
                  label={t('page.createAccount.button.create')}
                  variant={EButtonVariants.contained}
                  isLoading={isCreateLoading}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  id="signin"
                  label={t('page.createAccount.button.cancel')}
                  variant={EButtonVariants.outlined}
                  isLoading={isCreateLoading}
                  fullWidth
                  onClick={() => changePage(1)}
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </AuthTemplate>
  );
};

export { CreateAnAccount };
